// String-Auslegung-Panel — extrahiert aus InquiriesPage.js für bessere
// Modularisierung. Identische Funktionalität: KI-Extraktion der PV-
// Designdaten aus Mail/PDF, manuelle Korrektur und PDF-Export der
// String-Auslegung.
import { useState, useEffect } from 'react';
import { Zap, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { authFetch } from '../../lib/auth-fetch';

export default function StringDesignPanel({ inquiry, onChange }) {
  const cd = inquiry?.pvDesign;
  const [extracting, setExtracting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Initial state: wenn schon extrahiert, übernehmen — sonst leer
  useEffect(() => {
    if (cd && !draft) {
      setDraft({
        moduleManufacturer: cd.moduleManufacturer || '',
        moduleType: cd.moduleType || '',
        annualConsumptionKwh: cd.annualConsumptionKwh || '',
        fields: (cd.fields || []).map((f, i) => ({
          fieldName: f.fieldName || `Feld ${i + 1}`,
          moduleCount: f.moduleCount || '',
          powerWpPerModule: f.powerWpPerModule || '',
          vocPerModule: f.vocPerModule || '',
          vmpPerModule: f.vmpPerModule || '',
          nominalCurrentPerModule: f.nominalCurrentPerModule || '',
          iscPerModule: f.iscPerModule || '',
          betaVocPercent: f.betaVocPercent || -0.27,
          minDesignTempC: f.minDesignTempC ?? -10,
          orientation: f.orientation || 'sued',
        })),
      });
    }
  }, [cd, draft]);

  const runExtraction = async () => {
    setExtracting(true); setErr(null);
    try {
      const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/extract-pv-design`, { method: 'POST' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Extraktion fehlgeschlagen');
      }
      onChange?.();
      setDraft(null);
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setExtracting(false); }
  };

  const updateField = (idx, key, value) => {
    setDraft(d => ({
      ...d,
      fields: d.fields.map((f, i) => i === idx ? { ...f, [key]: value } : f),
    }));
  };

  const addField = () => {
    setDraft(d => ({
      ...d,
      fields: [...(d.fields || []), {
        fieldName: `Feld ${(d.fields?.length || 0) + 1}`,
        moduleCount: '', powerWpPerModule: '', vocPerModule: '',
        vmpPerModule: '', nominalCurrentPerModule: '',
        betaVocPercent: -0.27, minDesignTempC: -10, orientation: 'sued',
      }],
    }));
  };

  const removeField = (idx) => {
    setDraft(d => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));
  };

  const generatePdf = async () => {
    if (!draft || !draft.fields?.length) {
      setErr('Mindestens ein Modulfeld erforderlich.');
      return;
    }
    setGenerating(true); setErr(null);
    try {
      const [{ findBestPvSystemSolutions }, { exportPvPdf }] = await Promise.all([
        import('../../lib/pv-sizing'),
        import('../../lib/pdf-export'),
      ]);
      const prepared = draft.fields.map((f, idx) => {
        const fieldName = f.fieldName || `Feld ${idx + 1}`;
        return {
          fieldName, stringName: fieldName,
          moduleCount: Number(f.moduleCount) || 0,
          powerWpPerModule: Number(f.powerWpPerModule) || 0,
          vocPerModule: Number(f.vocPerModule) || 0,
          vmpPerModule: f.vmpPerModule ? Number(f.vmpPerModule) : null,
          nominalCurrentPerModule: Number(f.nominalCurrentPerModule) || 0,
          iscPerModule: f.iscPerModule ? Number(f.iscPerModule) : (Number(f.nominalCurrentPerModule) || 0),
          betaVocPercent: Number(f.betaVocPercent ?? -0.27),
          minDesignTempC: Number(f.minDesignTempC ?? -10),
          orientation: f.orientation || 'sued',
        };
      });
      const annualKwh = draft.annualConsumptionKwh ? Number(draft.annualConsumptionKwh) : null;
      const result = findBestPvSystemSolutions(prepared, 'fields', annualKwh);
      if (!result?.solutions?.length) {
        setErr(result?.warnings?.[0] || 'Keine passende Lösung gefunden — Eingabewerte prüfen.');
        return;
      }
      result.solutions.forEach(sol => {
        sol.assignment?.assigned?.forEach(s => { s.displayName = s.displayName || s.stringName; });
        sol.assignment?.unassigned?.forEach(s => { s.displayName = s.displayName || s.stringName; });
      });
      exportPvPdf(result);
      await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/customer-data`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pvDesignDraft: draft }),
      });
    } catch (e) {
      setErr(e.message || 'PDF-Erzeugung fehlgeschlagen');
    } finally { setGenerating(false); }
  };

  const inputCls = 'text-[12px] px-2 py-1.5 rounded border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-[#9ec043]/40 w-full';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="string-design-panel">
      <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#9ec043]" />
          String-Auslegung & PDF
          {cd?.confidence && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
              cd.confidence === 'high'   ? 'bg-emerald-100 text-emerald-700' :
              cd.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                                           'bg-red-100 text-red-700'
            }`}>
              KI: {cd.confidence}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={runExtraction} disabled={extracting}
                  className="h-7 text-[11px]" data-testid="btn-pv-extract">
            <RefreshCw className={`w-3 h-3 mr-1 ${extracting ? 'animate-spin' : ''}`} />
            {extracting ? 'Extrahiert…' : (cd ? 'Neu extrahieren' : 'Aus Mail extrahieren')}
          </Button>
          {draft && (
            <Button size="sm" variant="outline" onClick={() => setEditing(e => !e)}
                    className="h-7 text-[11px]" data-testid="btn-pv-edit">
              {editing ? 'Felder fertig' : 'Felder anpassen'}
            </Button>
          )}
        </div>
      </div>

      {!cd && !extracting && (
        <p className="text-[11px] text-gray-400 italic">
          Klicke „Aus Mail extrahieren", damit die KI Modulanzahl, Wp, Voc, Ausrichtung etc. aus der Anfrage zieht. Du kannst die Werte danach prüfen und korrigieren, bevor das PDF erstellt wird.
        </p>
      )}

      {cd && draft && (
        <>
          {cd.rationale && (
            <p className="text-[10px] text-gray-500 italic mb-2">{cd.rationale}</p>
          )}
          {(cd.moduleManufacturer || cd.moduleType) && (
            <div className="text-[11px] text-gray-600 mb-2">
              <strong>Modul:</strong> {cd.moduleManufacturer || '?'} {cd.moduleType || ''}
            </div>
          )}

          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] uppercase tracking-wide text-gray-500">Modulhersteller
                  <input className={inputCls} value={draft.moduleManufacturer}
                         onChange={e => setDraft(d => ({ ...d, moduleManufacturer: e.target.value }))}
                         data-testid="pv-manufacturer" />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-gray-500">Modul-Typ
                  <input className={inputCls} value={draft.moduleType}
                         onChange={e => setDraft(d => ({ ...d, moduleType: e.target.value }))}
                         data-testid="pv-module-type" />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-gray-500">Jahresverbrauch (kWh, optional)
                  <input className={inputCls} type="number" value={draft.annualConsumptionKwh}
                         onChange={e => setDraft(d => ({ ...d, annualConsumptionKwh: e.target.value }))}
                         data-testid="pv-annual-kwh" />
                </label>
              </div>
              {draft.fields.map((f, idx) => (
                <div key={idx} className="border border-gray-100 rounded-md p-2 bg-gray-50/40"
                     data-testid={`pv-field-${idx}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input className={`${inputCls} flex-1`} value={f.fieldName}
                           onChange={e => updateField(idx, 'fieldName', e.target.value)}
                           placeholder="Feldname" />
                    {draft.fields.length > 1 && (
                      <button onClick={() => removeField(idx)}
                              className="text-rose-500 hover:text-rose-700"
                              data-testid={`pv-field-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Anzahl
                      <input className={inputCls} type="number" value={f.moduleCount}
                             onChange={e => updateField(idx, 'moduleCount', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Wp/Modul
                      <input className={inputCls} type="number" value={f.powerWpPerModule}
                             onChange={e => updateField(idx, 'powerWpPerModule', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Voc/Modul (V)
                      <input className={inputCls} type="number" step="0.01" value={f.vocPerModule}
                             onChange={e => updateField(idx, 'vocPerModule', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Vmp/Modul (V)
                      <input className={inputCls} type="number" step="0.01" value={f.vmpPerModule}
                             onChange={e => updateField(idx, 'vmpPerModule', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Imp/Modul (A)
                      <input className={inputCls} type="number" step="0.01" value={f.nominalCurrentPerModule}
                             onChange={e => updateField(idx, 'nominalCurrentPerModule', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">β Voc (%/K)
                      <input className={inputCls} type="number" step="0.01" value={f.betaVocPercent}
                             onChange={e => updateField(idx, 'betaVocPercent', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Tmin (°C)
                      <input className={inputCls} type="number" value={f.minDesignTempC}
                             onChange={e => updateField(idx, 'minDesignTempC', e.target.value)} /></label>
                    <label className="text-[10px] uppercase tracking-wide text-gray-500">Ausrichtung
                      <select className={inputCls} value={f.orientation}
                              onChange={e => updateField(idx, 'orientation', e.target.value)}>
                        <option value="sued">Süd</option>
                        <option value="sued_ost">Süd-Ost</option>
                        <option value="sued_west">Süd-West</option>
                        <option value="ost">Ost</option>
                        <option value="west">West</option>
                        <option value="ost_west">Ost-West</option>
                        <option value="nord">Nord</option>
                        <option value="flach">Flach</option>
                      </select>
                    </label>
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addField}
                      className="h-7 text-[11px]" data-testid="btn-pv-add-field">
                + Feld hinzufügen
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              {draft.fields.map((f, idx) => (
                <div key={idx} className="text-[12px] text-gray-700 flex items-center gap-2 flex-wrap"
                     data-testid={`pv-field-readonly-${idx}`}>
                  <span className="font-semibold">{f.fieldName}:</span>
                  <span>{f.moduleCount || '?'} × {f.powerWpPerModule || '?'} Wp</span>
                  <span className="text-gray-400">· {f.vocPerModule || '?'} V Voc · {f.orientation}</span>
                </div>
              ))}
            </div>
          )}

          {err && (
            <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1"
                 data-testid="pv-error">{err}</div>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" onClick={generatePdf} disabled={generating}
                    className="h-8 text-xs bg-[#9ec043] hover:bg-[#8aad38] text-white"
                    data-testid="btn-pv-generate-pdf">
              {generating ? 'Erzeugt PDF…' : 'String-Auslegung als PDF herunterladen'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
